import {
  Children,
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ComponentRef,
} from "react";

import { RadioIndicator } from "../../atoms/radio-button";
import { radioButtonHitSlop } from "../../atoms/radio-button/lib";
import { Typography } from "../../atoms/typography";
import { cn } from "../../lib";
import { touchTargetClassName } from "../../lib/touch-target";
import { Box, Pressable } from "../../primitives";
import type {
  RadioGroupIndicator,
  RadioGroupItemProps,
  RadioGroupOrientation,
  RadioGroupRootProps,
  RadioGroupSize,
} from "./types";
import { useRadioGroupKeyboard } from "./use-radio-group-keyboard";

// ---------------------------------------------------------------------------
// Layout — design-token spacing (see tokens/spacing)
// ---------------------------------------------------------------------------

// Vertical items get no gap: each row is at least 44px tall and pads itself.
// Horizontal items are 12 apart, as Figma binds padding-md (5441:7719)
const orientationClasses: Record<RadioGroupOrientation, string> = {
  horizontal: "flex-row flex-wrap gap-spacing-md",
  vertical: "flex-col",
};

const labelVariants = {
  sm: "body-sm",
  md: "body-md",
} as const satisfies Record<RadioGroupSize, string>;

// The indicator's width per style and size (radioButtonVariants), so custom
// children line up with the label
const indicatorWidthClasses = {
  fill: { sm: "w-radio-sm md:w-radio-sm-dt", md: "w-radio-md md:w-radio-md-dt" },
  check: {
    sm: "w-radio-check-sm md:w-radio-check-sm-dt",
    md: "w-radio-check-md md:w-radio-check-md-dt",
  },
} as const satisfies Record<RadioGroupIndicator, Record<RadioGroupSize, string>>;

// ---------------------------------------------------------------------------
// Selection propagation
//
// RadioGroup shares its selection through context, so a RadioGroupItem works
// anywhere inside it, including inside another component, such as a ListItem
// slot. An item outside any RadioGroup is unchecked and selects nothing.
// ---------------------------------------------------------------------------

interface RadioGroupContextValue {
  value: string;
  onSelect: (value: string) => void;
  disabled: boolean;
  size: RadioGroupSize;
  indicator: RadioGroupIndicator;
}

const RadioGroupContext = createContext<RadioGroupContextValue | null>(null);

// ---------------------------------------------------------------------------
// Root
// ---------------------------------------------------------------------------

const RadioGroup = ({
  value: valueProp,
  defaultValue,
  onValueChange,
  disabled = false,
  orientation = "vertical",
  size = "md",
  indicator = "fill",
  className,
  testID,
  accessibilityLabel,
  onBlur,
  children,
}: RadioGroupRootProps) => {
  const rootRef = useRef<ComponentRef<typeof Box>>(null);
  useRadioGroupKeyboard(rootRef, onBlur);
  // The mode is decided on every render (J1): `value` given, even "" (no
  // selection), is controlled; `undefined` is uncontrolled
  const isControlled = valueProp !== undefined;
  const [uncontrolled, setUncontrolled] = useState(defaultValue ?? "");
  const value = isControlled ? valueProp : uncontrolled;

  const handleSelect = useCallback(
    (next: string) => {
      if (!isControlled) setUncontrolled(next);
      onValueChange?.(next);
    },
    [isControlled, onValueChange],
  );

  const group = useMemo<RadioGroupContextValue>(
    () => ({ value, onSelect: handleSelect, disabled, size, indicator }),
    [value, handleSelect, disabled, size, indicator],
  );

  // With no children at all (options still loading), screen readers don't
  // meet an empty, named group (RadioGroup #12). The root stays mounted, so
  // the keyboard model stays attached; children that render nothing can't be
  // seen from here, and aren't covered
  const isEmpty = Children.toArray(children).length === 0;

  return (
    <RadioGroupContext.Provider value={group}>
      <Box
        ref={rootRef}
        accessibilityRole={isEmpty ? undefined : "radiogroup"}
        accessibilityLabel={isEmpty ? undefined : accessibilityLabel}
        aria-hidden={isEmpty || undefined}
        testID={testID}
        className={cn("flex", orientationClasses[orientation], className)}
      >
        {children}
      </Box>
    </RadioGroupContext.Provider>
  );
};

RadioGroup.displayName = "RadioGroup";

// ---------------------------------------------------------------------------
// Item — full row per DS-023 (radio + optional label).
//
// The row is the radio: one Pressable holds a decorative RadioIndicator and
// the label, so a tap anywhere on it selects, on both platforms. A row with a
// label is at least 44px tall (DS-023 touch target); one without takes taps 44
// across as a standalone RadioButton does (RadioGroup #5). `label` is plain
// content; anything interactive goes in `children`, which render under the
// row, outside it. There's no description: neither Figma nor any screen used
// one.
// ---------------------------------------------------------------------------

// The ref is the row's Pressable: FormRadioGroup focuses it on a failed submit
const RadioGroupItem = forwardRef<ComponentRef<typeof Pressable>, RadioGroupItemProps>(
  (
    {
      value,
      label,
      id,
      disabled,
      size,
      indicator,
      className,
      testID,
      accessibilityLabel,
      children,
    },
    ref,
  ) => {
    const group = useContext(RadioGroupContext);
    const checked = group != null && group.value === value;
    const isDisabled = (disabled ?? false) || (group?.disabled ?? false);
    const effectiveSize = size ?? group?.size ?? "md";
    const effectiveIndicator = indicator ?? group?.indicator ?? "fill";
    const hasText = label != null;

    const handlePress = () => {
      // Pressing the selected item again changes nothing
      if (isDisabled || checked) return;
      group?.onSelect(value);
    };

    return (
      <Box className={cn("flex flex-col", className)}>
        <Pressable
          ref={ref}
          id={id}
          testID={testID}
          accessibilityRole="radio"
          accessibilityState={{ checked, disabled: isDisabled }}
          accessibilityLabel={accessibilityLabel ?? (typeof label === "string" ? label : undefined)}
          disabled={isDisabled}
          onClick={handlePress}
          hitSlop={hasText ? undefined : radioButtonHitSlop[effectiveIndicator][effectiveSize]}
          className={cn(
            "web:outline-none group flex flex-col justify-center",
            hasText ? "py-spacing-sm min-h-touch-target" : touchTargetClassName,
          )}
        >
          <Box className="gap-spacing-sm flex flex-row items-center">
            {/* The row takes keyboard focus, so the indicator rings for it */}
            <RadioIndicator
              checked={checked}
              disabled={isDisabled}
              size={effectiveSize}
              indicator={effectiveIndicator}
              groupFocus
            />
            {hasText ? (
              // min-w-0 shrink: on iOS and Android a long label otherwise ran
              // past the row's edge instead of wrapping beside the indicator
              <Box className="flex min-w-0 shrink flex-col">
                {/* Disabled text is attenuated, as Figma binds the label
                    (5441:7812; RadioGroup #9) */}
                <Typography
                  variant={labelVariants[effectiveSize]}
                  color={
                    isDisabled ? "neutral-foreground-attenuated" : "neutral-foreground-default"
                  }
                >
                  {label}
                </Typography>
              </Box>
            ) : null}
          </Box>
        </Pressable>
        {children != null ? (
          <Box className="gap-spacing-sm flex flex-row">
            <Box className={indicatorWidthClasses[effectiveIndicator][effectiveSize]} />
            <Box className="flex flex-1 flex-col">{children}</Box>
          </Box>
        ) : null}
      </Box>
    );
  },
);

RadioGroupItem.displayName = "RadioGroupItem";

// ---------------------------------------------------------------------------
// Exports
// ---------------------------------------------------------------------------

export { RadioGroup, RadioGroupItem };
